import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation, useHistory } from 'react-router-dom';
import { searchRoundReportStorageItems, setSearchParameters } from 'actions/admin/roundReportStorageItems';
import { performSearch, mergeSearchParameters } from 'components/adminTable/AdminTableHelper';
import roundReportStorageItemAdminStatuses from 'enums/roundReportStorageItemAdminStatuses';
import routes from 'routes';

import Loader from 'common/Loader';
import Box from '@material-ui/core/Box';
import AdminPage from '../../presentational/AdminPage';
import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import SearchRoundReportStorageItemsForm from './SearchRoundReportStorageItemsForm';
import AdminTable from 'components/adminTable/AdminTable';
import ShortDateTime from 'common/ShortDateTime';

const RoundReportStorageItems = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const searchResult = useSelector(state => state.admin.roundReportStorageItems.searchResult);
    const searchParametersFromState = useSelector(state => state.admin.roundReportStorageItems.searchParameters);

    const defaultParameters = {
        page: 0,
        pageSize: 20,
        occupiedInSystem: false,
        occupiedInReality: true,
        isMostRecent: true,
        adminStatuses: [roundReportStorageItemAdminStatuses.notStarted.key, roundReportStorageItemAdminStatuses.processing.key],
        sortByColumn: 'modifiedTime',
        sortDirection: 'desc'
    };

    const columns = [
        {
            title: 'Storage site',
            id: 'storageSiteTitle',
            render: item => <Link to={routes.admin.storageSite.replace(':storageSiteId', item.storageSiteId)}>{item.storageSiteTitle}</Link>
        },
        {
            title: 'Storage',
            id: 'storageTitle',
            render: item => <Link to={routes.admin.roundReportStorageItem.replace(':roundReportStorageItemId', item.id)}>{item.storageTitle}</Link>
        },
        {
            title: 'Modified',
            id: 'modifiedTime',
            render: item => <ShortDateTime value={item.modifiedTime} />
        },
        {
            title: 'Status',
            id: 'adminStatus',
            render: item => item.adminStatus
        }
    ];

    const search = parameters => {
        const searchParameters = mergeSearchParameters(parameters, location, searchParametersFromState, defaultParameters);
        performSearch(performSearchObject, searchParameters);
    };

    const getParametersForApiCall = parameters => ({
        ...parameters,
        adminStatuses: parameters.adminStatuses.join(',')
    });

    const performSearchObject = {
        searchParametersFromState,
        setSearchParametersToState: parameters => dispatch(setSearchParameters(parameters)),
        searchResult,
        search,
        apiCall: parameters => dispatch(searchRoundReportStorageItems(getParametersForApiCall(parameters))),
        location,
        history,
        isInitializedRef: useRef()
    };

    useEffect(() => {
        const searchParameters = mergeSearchParameters({}, location, searchParametersFromState, defaultParameters);
        performSearch(performSearchObject, searchParameters);
    }, []);

    const handleSearchFormSubmit = formValues => {
        search({ ...defaultParameters, ...formValues, page: 0 });
    };

    const handleSort = sortingParameters => {
        search(sortingParameters);
    };

    const handlePageChange = newPage => {
        search({ page: newPage });
    };

    const handlePageChangeSize = newPageSize => {
        search({ page: 0, pageSize: newPageSize });
    };

    return (
        <AdminPage title="Round report storage items">
            <AdminContentWrapper type="listing">

                <SearchRoundReportStorageItemsForm
                    searchParameters={searchParametersFromState}
                    onSubmit={handleSearchFormSubmit}
                />

                <Box>
                    {
                        searchResult.isLoading && <Loader />
                    }
                    {
                        searchResult.items && (
                            <AdminTable
                                isPaged
                                items={searchResult.items}
                                count={searchResult.count}
                                page={searchResult.page}
                                pageSize={searchResult.pageSize}
                                columns={columns}
                                sortByColumn={columns.find(c => c.id === searchParametersFromState.sortByColumn)}
                                sortDirection={searchParametersFromState.sortDirection}
                                onSort={handleSort}
                                onPageChange={handlePageChange}
                                onPageChangeSize={handlePageChangeSize}
                            />
                        )
                    }
                </Box>

            </AdminContentWrapper>
        </AdminPage>
    );
};

export default RoundReportStorageItems;
